import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../lib/AuthContext';
import { supabase } from '../../lib/supabase-client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';

export function ResetPasswordPage() {
    const { resetPassword, isPasswordRecovery, clearPasswordRecovery } = useAuth();

    // Request mode state
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // Set new password mode state
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);

    const handleRequestReset = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        const { error } = await resetPassword(email);

        if (error) {
            setError(error.message);
            setLoading(false);
        } else {
            setSuccess(true);
            setLoading(false);
        }
    };

    const handleSetNewPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (newPassword.length < 8) {
            setError('Hasło musi mieć co najmniej 8 znaków');
            return;
        }
        if (newPassword !== confirmNewPassword) {
            setError('Hasła nie są zgodne');
            return;
        }

        setLoading(true);
        try {
            const { error } = await supabase.auth.updateUser({ password: newPassword });
            if (error) throw error;
            setPasswordResetSuccess(true);
            clearPasswordRecovery();
        } catch (err: any) {
            setError(err.message || 'Nie udało się zmienić hasła');
        } finally {
            setLoading(false);
        }
    };

    // Password reset success state
    if (passwordResetSuccess) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <div className="bg-grid-pattern" />
                <div className="w-full max-w-[400px] relative z-10">
                    <div className="bg-card rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-border/50 p-8 text-center">
                        <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle className="w-8 h-8 text-green-500" />
                        </div>
                        <h2 className="text-xl font-semibold mb-2">Hasło zostało zmienione!</h2>
                        <p className="text-muted-foreground text-sm mb-6">
                            Możesz teraz zalogować się używając nowego hasła.
                        </p>
                        <Link to="/login">
                            <Button className="w-full">
                                Zaloguj się
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Set new password mode (after clicking recovery link from email)
    if (isPasswordRecovery) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <div className="bg-grid-pattern" />

                <div className="w-full max-w-[400px] relative z-10">
                    {/* Logo */}
                    <div className="text-center mb-8">
                        <h1 className="text-[24px] tracking-[0.15em] font-medium mb-2">WYSKO</h1>
                        <p className="text-sm text-muted-foreground">
                            Ustaw nowe hasło
                        </p>
                    </div>

                    {/* Card */}
                    <div className="bg-card rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-border/50 p-8">
                        <p className="text-sm text-muted-foreground mb-6">
                            Wpisz nowe hasło dla swojego konta.
                        </p>

                        <form onSubmit={handleSetNewPassword} className="space-y-5">
                            {/* Error Alert */}
                            {error && (
                                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <span className="break-words">{error}</span>
                                </div>
                            )}

                            {/* New Password */}
                            <div className="space-y-2">
                                <Label htmlFor="newPassword" className="text-[13px]">
                                    Nowe hasło
                                </Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        id="newPassword"
                                        type={showPassword ? 'text' : 'password'}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="Min. 8 znaków"
                                        className="pl-10 pr-10 h-10"
                                        required
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="w-4 h-4" />
                                        ) : (
                                            <Eye className="w-4 h-4" />
                                        )}
                                    </button>
                                </div>
                                <p className="text-[11px] text-muted-foreground">
                                    Minimum 8 znaków
                                </p>
                            </div>

                            {/* Confirm New Password */}
                            <div className="space-y-2">
                                <Label htmlFor="confirmNewPassword" className="text-[13px]">
                                    Potwierdź nowe hasło
                                </Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        id="confirmNewPassword"
                                        type={showPassword ? 'text' : 'password'}
                                        value={confirmNewPassword}
                                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                                        placeholder="Powtórz hasło"
                                        className="pl-10 h-10"
                                        required
                                        autoComplete="new-password"
                                    />
                                </div>
                            </div>

                            {/* Submit */}
                            <Button
                                type="submit"
                                className="w-full h-10"
                                disabled={loading}
                            >
                                {loading ? (
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        <span>Zapisywanie...</span>
                                    </div>
                                ) : (
                                    'Ustaw nowe hasło'
                                )}
                            </Button>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    // Email sent success state
    if (success) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <div className="bg-grid-pattern" />
                <div className="w-full max-w-[400px] relative z-10">
                    <div className="bg-card rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-border/50 p-8 text-center">
                        <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle className="w-8 h-8 text-green-500" />
                        </div>
                        <h2 className="text-xl font-semibold mb-2">Sprawdź swoją skrzynkę!</h2>
                        <p className="text-muted-foreground text-sm mb-6">
                            Jeśli konto z adresem <strong>{email}</strong> istnieje,
                            otrzymasz link do zresetowania hasła.
                        </p>
                        <Link to="/login">
                            <Button variant="outline" className="w-full">
                                Powrót do logowania
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Request reset mode (default)
    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="bg-grid-pattern" />

            <div className="w-full max-w-[400px] relative z-10">
                {/* Back Link */}
                <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Powrót do logowania
                </Link>

                {/* Logo */}
                <div className="text-center mb-8">
                    <h1 className="text-[24px] tracking-[0.15em] font-medium mb-2">WYSKO</h1>
                    <p className="text-sm text-muted-foreground">
                        Zresetuj swoje hasło
                    </p>
                </div>

                {/* Card */}
                <div className="bg-card rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-border/50 p-8">
                    <p className="text-sm text-muted-foreground mb-6">
                        Podaj adres e-mail powiązany z Twoim kontem. Wyślemy Ci link do zresetowania hasła.
                    </p>

                    <form onSubmit={handleRequestReset} className="space-y-5">
                        {/* Error Alert */}
                        {error && (
                            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <span className="break-words">{error}</span>
                            </div>
                        )}

                        {/* Email */}
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-[13px]">
                                Adres e-mail
                            </Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="twoj@email.pl"
                                    className="pl-10 h-10"
                                    required
                                    autoComplete="email"
                                />
                            </div>
                        </div>

                        {/* Submit */}
                        <Button
                            type="submit"
                            className="w-full h-10"
                            disabled={loading}
                        >
                            {loading ? (
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    <span>Wysyłanie...</span>
                                </div>
                            ) : (
                                'Wyślij link resetujący'
                            )}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
}
